import {
  CloseCircleOutlined,
  DisconnectOutlined,
  EditOutlined,
  PlusOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@tms/shared';

import { api } from '@/api/client';
import { useApiMutation, useDebouncedValue, usePaged } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { createdAtColumn } from '@/components/createdAtColumn';
import { StickyTable } from '@/components/StickyTable';
import { TableCard } from '@/components/TableCard';
import { CATEGORY_LABEL, WAYBILL_STATUS_LABEL } from '@/lib/labels';

import { AssignRequestModal } from './transport-requests/AssignRequestModal';
import { TransportRequestFormModal } from './transport-requests/TransportRequestFormModal';
import {
  REQUEST_STATUS,
  routeText,
  type TransportRequestRow,
  type TransportRequestStatus,
} from './transport-requests/types';

const { RangePicker } = DatePicker;

type StatusFilterValue = TransportRequestStatus | 'ALL';

/**
 * Заявки на транспорт — рабочее место диспетчера.
 *
 * Заявку принимают со слов подразделения (звонок, служебная записка), а
 * выполняется она заданием в путевом листе. Статус ведёт сам лист: заявка
 * назначена, пока стоит в действующем листе, и выполнена, когда лист закрыт.
 */
export function TransportRequestsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.TRANSPORT_REQUEST_MANAGE);

  const [status, setStatus] = useState<StatusFilterValue>('NEW');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TransportRequestRow | null>(null);
  const [assigning, setAssigning] = useState<TransportRequestRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<TransportRequestRow | null>(null);
  const [cancelForm] = Form.useForm<{ reason: string }>();

  const filters = {
    ...(status !== 'ALL' && { status }),
    dateFrom: dateRange?.[0],
    dateTo: dateRange?.[1],
    search: debouncedSearch || undefined,
  };
  const query = usePaged<TransportRequestRow>(['transport-requests'], '/transport-requests', {
    page,
    pageSize,
    ...filters,
  });

  const unassign = useApiMutation(
    async (id: number) => (await api.post(`/transport-requests/${id}/unassign`)).data,
    { successMessage: t('Заявка снята с путевого листа'), invalidate: [['transport-requests'], ['waybills']] },
  );
  const cancel = useApiMutation(
    async ({ id, reason }: { id: number; reason: string }) =>
      (await api.post(`/transport-requests/${id}/cancel`, { reason })).data,
    { successMessage: t('Заявка отменена'), invalidate: [['transport-requests']] },
  );

  if (!can(PERMISSIONS.TRANSPORT_REQUEST_READ)) {
    return <Typography.Text type="danger">{t('Нет прав на просмотр заявок')}</Typography.Text>;
  }

  return (
    <TableCard
      title={t('Заявки на транспорт')}
      extra={
        <Space wrap>
          <Select<StatusFilterValue>
            value={status}
            style={{ width: 150 }}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            options={[
              { value: 'NEW', label: t('Новые') },
              { value: 'ASSIGNED', label: t('Назначены') },
              { value: 'DONE', label: t('Выполнены') },
              { value: 'CANCELLED', label: t('Отменены') },
              { value: 'ALL', label: t('Все') },
            ]}
          />
          <Input.Search
            allowClear
            placeholder={t('Номер, заказчик, маршрут')}
            style={{ width: 220 }}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
          <RangePicker
            format="DD.MM.YYYY"
            onChange={(dates) => {
              // Границы — моментами начала и конца местных суток: сервер
              // сравнивает их со временем подачи как есть.
              setDateRange(
                dates?.[0] && dates[1]
                  ? [dates[0].startOf('day').toISOString(), dates[1].endOf('day').toISOString()]
                  : null,
              );
              setPage(1);
            }}
          />
          {canManage && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              {t('Принять заявку')}
            </Button>
          )}
        </Space>
      }
    >
      <StickyTable<TransportRequestRow>
        rowKey="id"
        rowNumbers
        exportSource={{ url: '/transport-requests', params: filters }}
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${t('Всего:')} ${total}`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
        columns={[
          { title: t('Номер'), dataIndex: 'number', width: 170 },
          {
            title: t('Статус'),
            width: 120,
            render: (_: unknown, row: TransportRequestRow) => (
              <Tooltip title={row.status === 'CANCELLED' ? row.cancelReason : undefined}>
                <Tag bordered={false} color={REQUEST_STATUS[row.status].color}>
                  {t(REQUEST_STATUS[row.status].label)}
                </Tag>
              </Tooltip>
            ),
          },
          {
            title: t('Когда'),
            width: 170,
            render: (_: unknown, row: TransportRequestRow) =>
              `${dayjs(row.neededFrom).format('DD.MM.YYYY HH:mm')}${row.neededTo ? ` — ${dayjs(row.neededTo).format(dayjs(row.neededTo).isSame(row.neededFrom, 'day') ? 'HH:mm' : 'DD.MM HH:mm')}` : ''}`,
          },
          {
            title: t('Заказчик'),
            width: 230,
            render: (_: unknown, row: TransportRequestRow) => (
              <Space direction="vertical" size={0}>
                <span>{row.requesterName}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {[row.department?.name, row.requesterPhone].filter(Boolean).join(' · ') || ' '}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t('Маршрут'),
            render: (_: unknown, row: TransportRequestRow) => (
              <Space direction="vertical" size={0}>
                <span>{routeText(row)}</span>
                {row.purpose && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {row.purpose}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          {
            title: t('Нужна техника'),
            width: 190,
            render: (_: unknown, row: TransportRequestRow) =>
              [
                row.vehicleCategory ? t(CATEGORY_LABEL[row.vehicleCategory] ?? row.vehicleCategory) : null,
                row.passengers ? `${row.passengers} ${t('пасс.')}` : null,
                row.cargoTons ? `${Number(row.cargoTons)} ${t('т')}` : null,
              ]
                .filter(Boolean)
                .join(' · ') || '—',
          },
          {
            title: t('Путевой лист'),
            width: 260,
            render: (_: unknown, row: TransportRequestRow) => {
              const waybill = row.task?.waybill;
              if (!waybill) return '—';
              const driver = waybill.driver.employee;
              return (
                <Space direction="vertical" size={0}>
                  <Space size={6}>
                    <span>{waybill.number}</span>
                    <Tag bordered={false} style={{ margin: 0 }}>
                      {t(WAYBILL_STATUS_LABEL[waybill.status] ?? waybill.status)}
                    </Tag>
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {waybill.vehicle.garageNumber}
                    {waybill.vehicle.plateNumber ? ` · ${waybill.vehicle.plateNumber}` : ''} ·{' '}
                    {driver.lastName} {driver.firstName}
                  </Typography.Text>
                </Space>
              );
            },
          },
          createdAtColumn<TransportRequestRow>(t),
          {
            title: '',
            width: 150,
            render: (_: unknown, row: TransportRequestRow) =>
              canManage && (
                <Space size={0}>
                  {row.status === 'NEW' && (
                    <>
                      <Tooltip title={t('Назначить машину')}>
                        <Button type="text" icon={<SendOutlined />} onClick={() => setAssigning(row)} />
                      </Tooltip>
                      <Tooltip title={t('Изменить')}>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditing(row);
                            setFormOpen(true);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={t('Отменить заявку')}>
                        <Button
                          type="text"
                          danger
                          icon={<CloseCircleOutlined />}
                          onClick={() => {
                            cancelForm.resetFields();
                            setCancelTarget(row);
                          }}
                        />
                      </Tooltip>
                    </>
                  )}
                  {row.status === 'ASSIGNED' && (
                    <Popconfirm
                      title={t('Снять заявку с путевого листа?')}
                      description={
                        row.task?.waybill.status === 'DRAFT'
                          ? t('Задание будет удалено из листа, заявка снова будет ждать машину.')
                          : t('Задание будет удалено из выданного листа — перепечатайте лист и сообщите водителю.')
                      }
                      okText={t('Снять')}
                      cancelText={t('Отмена')}
                      onConfirm={() => unassign.mutate(row.id)}
                    >
                      <Tooltip title={t('Снять с путевого листа')}>
                        <Button type="text" icon={<DisconnectOutlined />} />
                      </Tooltip>
                    </Popconfirm>
                  )}
                </Space>
              ),
          },
        ]}
      />

      <TransportRequestFormModal open={formOpen} editing={editing} onClose={() => setFormOpen(false)} />
      <AssignRequestModal request={assigning} onClose={() => setAssigning(null)} />

      <Modal
        open={cancelTarget !== null}
        title={cancelTarget ? `${t('Отменить заявку')} ${cancelTarget.number}` : ''}
        okText={t('Отменить заявку')}
        okButtonProps={{ danger: true }}
        cancelText={t('Закрыть')}
        confirmLoading={cancel.isPending}
        onCancel={() => setCancelTarget(null)}
        onOk={() =>
          void cancelForm.validateFields().then(({ reason }) =>
            cancel.mutate({ id: cancelTarget!.id, reason }, { onSuccess: () => setCancelTarget(null) }),
          )
        }
        destroyOnHidden
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            name="reason"
            label={t('Причина')}
            rules={[{ required: true, min: 3, message: t('Укажите причину') }]}
          >
            <Input.TextArea maxLength={400} autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
    </TableCard>
  );
}
